import React, { FC, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { CollectionFormSelectDropdownMenu } from './CollectionFormSelectDropdownMenu';
import { CollectionFormSelectDrawer } from './CollectionFormSelectDrawer';
import {
  GetChildPropertyOptionsResponse,
  Property,
  PropertyOption,
} from '@/api/types/categories.types';
import { Select } from '@/components/UI/Select/Select';
import { GET_CHILD_PROPERTY_OPTIONS } from '@/api/queries/categories.queries';
import { AdFormFields, Collection } from '@/components/AdForm/AdForm.types';
import {
  getChildrenPropertyIdsByPropertyValue,
  groupByPropertyLegacyId,
} from '@/components/Filters/CollectionSelect.helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import useScreenSize from '@/hooks/useScreenSize';
import { SelectRef } from '@/components/UI/Select/Select.types';

interface Props {
  propertyLegacyId: string;
  options: PropertyOption[];
  popularOptions?: PropertyOption[];
  collection: Collection;
  allProperties: Property[];
  onSelectPropertyOption?: (option: PropertyOption) => void;
}

export const CollectionFormSelect: FC<Props> = ({
  propertyLegacyId,
  options,
  popularOptions,
  collection,
  allProperties,
  onSelectPropertyOption,
}) => {
  const { name: label, isRequired } =
    allProperties?.find(el => el.legacyResourceId === propertyLegacyId) || {};
  const { control, setValue } = useFormContext<AdFormFields>();
  const { isLarge } = useScreenSize();
  const {
    field: { name, onChange, ...field },
    fieldState,
  } = useController({
    name: `collection.${propertyLegacyId}`,
    control,
    shouldUnregister: true,
  });
  const value = field.value as PropertyOption;
  const [childPropertyOptions, setChildPropertyOptions] = useState<
    PropertyOption[]
  >([]);
  const [isOpen, toggle] = useToggleOpenState();
  const [query, setQuery] = useState('');

  const selectRef = useRef<SelectRef>(null);
  const visibleValue = options.find(
    option => option.legacyResourceId === value?.legacyResourceId
  )?.name;
  const allPropertiesIds =
    allProperties?.map(property => property.legacyResourceId) || [];
  const groupedChildOptions = groupByPropertyLegacyId(
    childPropertyOptions
  ).sort(
    // TODO: This is a temporary solution for sorting, it is better to sort childrenPropertyIds on the backend side
    ([{ propertyLegacyId: a }], [{ propertyLegacyId: b }]) =>
      allPropertiesIds.indexOf(a) - allPropertiesIds.indexOf(b)
  );

  const { refetch: getChildPropertyOptions } =
    useQuery<GetChildPropertyOptionsResponse>(GET_CHILD_PROPERTY_OPTIONS, {
      skip: true,
      errorPolicy: 'all',
    });

  function clearCollection(propertyValue: string | undefined) {
    const allChildrenPropertyIds: string[] = [];
    const childrenPropertyIds = getChildrenPropertyIdsByPropertyValue(
      options,
      propertyValue
    );

    if (childrenPropertyIds?.length) {
      allChildrenPropertyIds.push(...childrenPropertyIds);
      childrenPropertyIds.forEach(id => {
        const ids = getChildrenPropertyIdsByPropertyValue(
          childPropertyOptions,
          (collection[id] as PropertyOption)?.legacyResourceId
        );

        if (ids?.length) {
          allChildrenPropertyIds.push(...ids);
        }
      });
    }

    for (const childPropertyId of allChildrenPropertyIds) {
      setValue(`collection.${childPropertyId}`, undefined);
    }
  }

  async function onItemClick(option: PropertyOption) {
    setChildPropertyOptions([]);

    clearCollection(
      (collection[propertyLegacyId] as PropertyOption)?.legacyResourceId
    );

    onChange(option);
    onSelectPropertyOption?.(option);
    toggle(false);
    selectRef.current?.clearSearchValue();

    for await (const childrenPropertyId of option.childrenPropertyIds) {
      const { data } = await getChildPropertyOptions({
        legacyId: childrenPropertyId,
        parentLegacyId: option.legacyResourceId,
        includePhotos: true,
      });

      setChildPropertyOptions(prev => [...prev, ...data.property.options]);
    }
  }

  if (!label || !options.length) return null;

  return (
    <>
      <Select
        name={name}
        isOpen={isOpen}
        toggle={toggle}
        ref={selectRef}
        isRequired={isRequired}
        appearance="filled"
        value={value?.legacyResourceId ?? null}
        visibleValue={visibleValue}
        label={label}
        helperText={fieldState.error?.message}
        hasError={!!fieldState.error}
        hasSearch={isLarge}
        onSearch={setQuery}
        hasDropdown={isLarge}
      >
        {isLarge ? (
          <CollectionFormSelectDropdownMenu
            isOpen={isOpen}
            query={query}
            options={options}
            onItemClick={onItemClick}
            collection={collection}
            popularOptions={popularOptions}
          />
        ) : (
          <CollectionFormSelectDrawer
            isOpen={isOpen}
            toggle={toggle}
            propertyLegacyId={propertyLegacyId}
            options={options}
            popularOptions={popularOptions}
            onItemClick={onItemClick}
            collection={collection}
            allProperties={allProperties}
          />
        )}
      </Select>

      {groupedChildOptions.map(group => {
        return (
          <CollectionFormSelect
            key={group[0].propertyLegacyId}
            propertyLegacyId={group[0].propertyLegacyId}
            options={group}
            collection={collection}
            allProperties={allProperties}
            onSelectPropertyOption={onSelectPropertyOption}
          />
        );
      })}
    </>
  );
};

CollectionFormSelect.displayName = `CollectionFormSelect`;
